import { yupResolver } from '@hookform/resolvers/yup';
import { isSupported } from 'firebase/messaging';
import * as yup from 'yup';

import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import Button from 'components/field/Button';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Typography from 'components/global/Typography';

import useAlert from 'hooks/useAlert';
import { appTrackingData } from 'hooks/useAppTracking';
import useChangeLanguage from 'hooks/useChangeLanguage';
import useLocale from 'hooks/useLocale';
import useRefreshToken from 'hooks/useRefreshToken';

import axios from 'services/axios';
import { REGISTER } from 'services/consts';
import { deleteFCMToken } from 'services/firebase';

import AppLogger from 'utils/AppLogger';
import { languages } from 'utils/localization';
import PATHS from 'utils/paths';

import * as S from './Common.styled';
import RegisterFirstStep from './RegisterFirstStep';
import RegisterSecondStep from './RegisterSecondStep';
import useFedCM from './useFedCM';

const titles = {
	/* eslint-disable camelcase */
	en_US: 'Sign Up for a Free OTO Account',
	ar_SA: 'انشئ حساب مجانًا في أوتو',
	tr_TR: 'Ücretsiz OTO Hesabı Oluşturun'
	/* eslint-enable camelcase */
};

const Register = () => {
	const { t } = useTranslation();
	const schema = useMemo(
		() => [
			yup.object().shape({
				mail: yup.string().email().nullable().required(),
				password: yup
					.string()
					.nullable()
					.required()
					.matches(/^(?=.{8,})/, t('passwordError.8Character'))
					.matches(/^(?=.*[a-z])/, t('passwordError.lowerCase'))
					.matches(/^(?=.*[A-Z])/, t('passwordError.upperCase'))
					.matches(/^(?=.*\d)/, t('passwordError.oneNumber'))
			}),
			yup.object().shape({
				// Register Details
				firstName: yup.string().nullable().required(),
				lastName: yup.string().nullable().required(),
				companyName: yup
					.string()
					.nullable()
					.when(['haveCompanyName'], {
						is: (haveCompanyName) => haveCompanyName === true,
						then: (schema) => schema.required()
					}),
				countryID: yup.number().nullable().required(),
				phone: yup.string().nullable().required().phoneNumber(),
				promoCode: yup.string().nullable(),
				haveCompanyName: yup.boolean().nullable(),
				havePromoCode: yup.boolean().nullable()
			})
		],
		[t]
	);

	const { showError } = useAlert();
	const [loading, setLoading] = useState(false);
	const refreshAccess = useRefreshToken();
	const { lang } = useParams();
	const { LangCountryCode } = useLocale();
	const changeLanguage = useChangeLanguage();
	const { state } = useLocation();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const referralToken = searchParams?.get('referralToken');
	const otoProduct = searchParams?.get('product');

	const [tab, setTab] = useState(0);
	useFedCM();

	const nextStep = async (data) => {
		if (tab < 1) {
			setLoading(true);

			await axios
				.post('/web/v1/register/verifyEmail', {
					email: data.mail
				})
				.then(async (response) => {
					const { status } = response;
					if (status === 200) {
						if (response.data.verified) {
							setTab(tab + 1);
						}
					} else {
						AppLogger.error(response);
					}
				})
				.catch((error) => {
					const errorCode = error?.response?.data?.code;

					const ErrorFields = {
						'error.G002': 'companyName',
						'error.G003': 'mail',
						'error.G008': 'phone'
					};

					const fieldName = ErrorFields[errorCode];
					fieldName && setError(fieldName, { type: 'custom', message: errorCode }, { shouldFocus: true });
					!fieldName && AppLogger.error(error);

					showError({
						header: 'error',
						message: errorCode
					});
				});

			setLoading(false);
		}
	};

	useEffect(() => {
		const language = languages.find((language) => language.systemCode === lang);
		if (language) {
			if (language !== LangCountryCode) changeLanguage(language.code);
		} else {
			navigate(
				{ pathname: `/register/${LangCountryCode}`, search: searchParams.toString() },
				{
					state: { redirectTo: state?.redirectTo }
				}
			);
		}
	}, [lang]);

	useEffect(() => {
		document.title = titles[LangCountryCode];
	}, [LangCountryCode]);

	const formMethods = useForm({
		resolver: yupResolver(schema[tab]),
		defaultValues: {
			mail: null,
			password: null,
			firstName: null,
			lastName: null,
			companyName: null,
			countryID: null,
			phone: null,
			promoCode: null
		}
	});

	const {
		handleSubmit,
		setError,
		formState: { isDirty }
	} = formMethods;

	const onSubmit = async (data) => {
		setLoading(true);

		await axios
			.post(REGISTER.REGISTER_USER, {
				...data,
				email: data.mail,
				aggreedTerms: true,
				locale: LangCountryCode,
				referralToken,
				otoProduct
			})
			.then(async (response) => {
				const { status } = response;
				if (status === 200) {
					appTrackingData.isNewRegistered = true;

					window.dataLayer = window.dataLayer || [];
					window.dataLayer.push({
						/* eslint-disable camelcase */
						event: 'signup',
						first_name: data?.firstName || '',
						last_name: data?.lastName || '',
						phone: data?.phone || '',
						email: data?.mail || '',
						country: data?.country || '',
						userID: response?.data?.userID || ''
						/* eslint-enable camelcase */
					});

					try {
						await isSupported().then(async (isSupported) => {
							isSupported && (await deleteFCMToken());
						});
					} catch {
						// do nothing
					}

					await refreshAccess();

					if (state?.redirectTo) {
						navigate(state.redirectTo.pathname + state.redirectTo.search);
					} else {
						navigate(PATHS.BASE);
					}
				} else {
					AppLogger.error(response);
					showError();
				}
			})
			.catch((error) => {
				if (error?.response?.data?.errors) {
					error.response.data.errors.forEach((err) => {
						const errorCode = err?.code;

						const ErrorFields = {
							'error.G002': 'companyName',
							'error.G003': 'mail',
							'error.G008': 'phone'
						};

						const fieldName = ErrorFields[errorCode];
						if (fieldName) {
							setError(fieldName, { type: 'custom', message: t(errorCode) }, { shouldFocus: true });
						} else {
							AppLogger.error(error);
							showError({
								message: errorCode
							});
						}
					});
				} else {
					AppLogger.error(error);
					showError({
						message: error?.response?.data?.code
					});
				}
			});

		setLoading(false);
	};

	return (
		<Form
			formMethods={formMethods}
			borderless
			style={{
				display: 'flex',
				justifyContent: 'center'
			}}>
			<S.Container>
				<S.Content>
					<div>{tab === 0 ? <RegisterFirstStep loading={loading} /> : <RegisterSecondStep />}</div>

					<FormItem style={{ paddingTop: '2rem' }}>
						{tab === 0 ? (
							<Button
								size="large"
								loading={loading}
								disabled={!isDirty}
								style={{ width: '100%' }}
								loadingLabel="signingUp"
								onMouseDown={handleSubmit(nextStep, (errors) => {
									AppLogger.log(errors);
								})}>
								<Typography className="button-text" fontWeight="500">
									createFreeAccount
								</Typography>
							</Button>
						) : (
							<Button
								size="large"
								loading={loading}
								disabled={!isDirty}
								style={{ width: '100%' }}
								loadingLabel="signingUp"
								onClick={handleSubmit(onSubmit, (errors) => {
									AppLogger.log(errors);
								})}>
								<Typography className="button-text" fontWeight="500">
									startShipping
								</Typography>
							</Button>
						)}
					</FormItem>
				</S.Content>
			</S.Container>
			<FormItem>
				<S.ButtonContainer>
					<Typography color="var(--black)">alreadyHaveAccount</Typography>
					<S.ButtonLink
						variant="transparent"
						label="signIn"
						disabled={loading}
						onClick={() => {
							navigate(
								{ pathname: `/login/${LangCountryCode}`, search: searchParams.toString() },
								{
									state: { redirectTo: state?.redirectTo }
								}
							);
						}}
					/>
				</S.ButtonContainer>
			</FormItem>
		</Form>
	);
};

export default Register;
